import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getObjectId } from '@/helpers/object.id.helper';
import { getEntityRefFromFlow } from '@/helpers/entity-ref.helper';
import {
  resolveScheduleLiveTimedSlotLock,
  type ScheduleLiveTimedSlotLock,
} from '@/helpers/schedule-live-slot-lock.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { usePlayerSocket } from '@/hooks/use-player-socket';
import {
  GET_LIVE_CONTEXT_STATE_QUERY_KEY,
  useGetLiveContextStateQuery,
} from '@/state/queries/player/use-get-live-context-state';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { useFlowControlPlayingSegmentExpandId } from './use-flow-control-playing-segment-expand-id';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { shouldRefetchQueriesOnLiveContextInvalidated } from '@/helpers/should-refetch-on-live-context-invalidated.helper';

const NO_LOCK: ScheduleLiveTimedSlotLock = {
  lastLockedTimedIndex: -1,
  lockedTimedSlotIds: new Set(),
};

function resolveSchedulePlayingKey(playingExpandId: string | null, activeSlotId: string | undefined): string | null {
  return playingExpandId ?? activeSlotId ?? null;
}

function isSameScheduleLock(a: ScheduleLiveTimedSlotLock, b: ScheduleLiveTimedSlotLock): boolean {
  if (a.lastLockedTimedIndex !== b.lastLockedTimedIndex) {
    return false;
  }
  if (a.lockedTimedSlotIds.size !== b.lockedTimedSlotIds.size) {
    return false;
  }
  for (const id of a.lockedTimedSlotIds) {
    if (!b.lockedTimedSlotIds.has(id)) {
      return false;
    }
  }
  return true;
}

/**
 * Schedule tab: lock timed slots at/before the control-tab playing segment while live.
 * Pass canonical timed slots from server flow (not optimistic localSlots) so locks survive failed DaD.
 */
export function useScheduleLiveSlotLock(
  canonicalTimedSlots: FlowSlotDto[],
  enabled: boolean,
): ScheduleLiveTimedSlotLock {
  const flow = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowToUse = flowPlan ?? flow;
  const liveRunning = Boolean(enabled && flowToUse?.liveContext?.isRunning);
  const planFlowId = flowToUse?.origin ? getObjectId(flowToUse.origin) : flowToUse?.id;
  const queryClient = useQueryClient();
  const [stickyLock, setStickyLock] = useState(NO_LOCK);

  const entityRef = useMemo(() => {
    if (!planFlowId || !liveRunning) {
      return undefined;
    }
    return getEntityRefFromFlow(planFlowId, flowToUse?.liveContext);
  }, [flowToUse?.liveContext, liveRunning, planFlowId]);

  const { data: liveContextState } = useGetLiveContextStateQuery(entityRef, {
    enabled: liveRunning && !!entityRef,
  });
  const cueState = useLiveContextCueState(entityRef, undefined, liveContextState?.cueState);

  const socketEntity =
    liveRunning && flowToUse?.liveContext?.entityType && flowToUse?.liveContext?.entityId
      ? {
          entityType: flowToUse.liveContext.entityType as 'Flow' | 'Event',
          entityId: flowToUse.liveContext.entityId,
        }
      : { entityType: 'Flow' as const, entityId: planFlowId ?? '' };

  usePlayerSocket(
    socketEntity.entityId,
    socketEntity.entityType,
    {
      onPlayersInvalidated: () => {},
      onLiveContextInvalidated: (_entityId, _entityType, meta) => {
        if (
          !shouldRefetchQueriesOnLiveContextInvalidated({
            liveIsRunning: liveRunning,
            isRunningFromEvent: meta?.isRunning,
          })
        ) {
          return;
        }
        if (entityRef) {
          queryClient.invalidateQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef] });
        }
      },
    },
    liveRunning && !!socketEntity.entityId,
  );

  const playingExpandId = useFlowControlPlayingSegmentExpandId();

  const computedLock = useMemo(() => {
    if (!liveRunning) {
      return NO_LOCK;
    }

    const playingKey = resolveSchedulePlayingKey(playingExpandId, cueState?.activeSlotId);
    const nextLock = resolveScheduleLiveTimedSlotLock(canonicalTimedSlots, playingKey);
    if (nextLock.lockedTimedSlotIds.size > 0) {
      return nextLock;
    }

    // Cue refetch / between-step gaps: keep the last known lock while still live.
    if (stickyLock.lockedTimedSlotIds.size > 0) {
      return stickyLock;
    }

    return nextLock;
  }, [liveRunning, playingExpandId, cueState?.activeSlotId, canonicalTimedSlots, stickyLock]);

  useEffect(() => {
    if (!liveRunning) {
      setStickyLock(NO_LOCK);
      return;
    }

    const playingKey = resolveSchedulePlayingKey(playingExpandId, cueState?.activeSlotId);
    const nextLock = resolveScheduleLiveTimedSlotLock(canonicalTimedSlots, playingKey);
    if (nextLock.lockedTimedSlotIds.size === 0) {
      return;
    }

    setStickyLock((prev) => (isSameScheduleLock(prev, nextLock) ? prev : nextLock));
  }, [liveRunning, playingExpandId, cueState?.activeSlotId, canonicalTimedSlots]);

  return computedLock;
}
